import { readRuntimeConfigValue } from './runtime-config';

export type TelegramLoginUser = {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
};

export type TelegramLoginContext = {
  botUsername: string;
  botId?: number;
  available: boolean;
};

const TELEGRAM_AUTH_KEYS = ['id', 'first_name', 'last_name', 'username', 'photo_url', 'auth_date', 'hash'] as const;

function parseTelegramBotUsername(value: string): string {
  const trimmed = value.trim();
  if (trimmed === '') {
    return '';
  }

  if (trimmed.startsWith('@')) {
    return trimmed.slice(1).split(/[/?#]/, 1)[0] ?? '';
  }

  try {
    const url = new URL(trimmed);
    if (url.hostname === 't.me' || url.hostname === 'telegram.me' || url.hostname === 'www.t.me') {
      return url.pathname.replace(/^\//, '').split('/', 1)[0] ?? '';
    }
  } catch {
    return trimmed.split(/[/?#]/, 1)[0] ?? '';
  }

  return '';
}

export function getTelegramLoginContext(): TelegramLoginContext {
  const botUsername = parseTelegramBotUsername(readRuntimeConfigValue('VITE_TELEGRAM_BOT_URL'));
  const rawBotId = Number(readRuntimeConfigValue('VITE_TELEGRAM_BOT_ID') || 0);
  const botId = Number.isFinite(rawBotId) && rawBotId > 0 ? rawBotId : undefined;

  return {
    botUsername,
    botId,
    available: botUsername !== '',
  };
}

export function serializeTelegramLoginData(user: TelegramLoginUser): string {
  const values = new URLSearchParams();
  values.set('id', String(user.id));
  values.set('first_name', user.first_name);
  values.set('auth_date', String(user.auth_date));
  values.set('hash', user.hash);

  if (user.last_name) {
    values.set('last_name', user.last_name);
  }
  if (user.username) {
    values.set('username', user.username);
  }
  if (user.photo_url) {
    values.set('photo_url', user.photo_url);
  }

  return values.toString();
}

export function extractTelegramLoginData(searchParams: URLSearchParams): string {
  const values = new URLSearchParams();

  for (const key of TELEGRAM_AUTH_KEYS) {
    const value = searchParams.get(key);
    if (value) {
      values.set(key, value);
    }
  }

  return values.toString();
}

export function hasTelegramLoginData(searchParams: URLSearchParams): boolean {
  return searchParams.has('id') && searchParams.has('auth_date') && searchParams.has('hash');
}

export function buildTelegramAuthUrl(pathname: string, params: Record<string, string | undefined> = {}): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value && value.trim() !== '') {
      search.set(key, value);
    }
  }

  const suffix = search.toString();
  const relativeUrl = suffix ? `${pathname}?${suffix}` : pathname;

  if (typeof window === 'undefined') {
    return relativeUrl;
  }

  return new URL(relativeUrl, window.location.origin).toString();
}
